import { get, max } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import PriceChangeModal from "shared/components/PriceChangeModal";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import useEffectiveDatePriceChange from "shared/hooks/useEffectiveDatePriceChange";
import {
  BUILD_GENERAL_COST,
  BUILD_GENERAL_COSTS,
} from "utils/api/graphql/mutations/costs";
import { UPDATE_PROJECT } from "utils/api/graphql/mutations/projects";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INSURANCE_PLAN } from "utils/api/graphql/queries/insurance-plans";
import { SUBSCRIPTION } from "utils/api/graphql/queries/subscription";
import { CASE_TYPES, INSURANCE_PLAN_PARAMS_RULES, KEYS } from "utils/constants";
import { generateSQLCondition } from "utils/helpers/graphql";
import Button from "../Components/Button";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

function findRules({ params, ruleName }) {
  let results = [];

  params.forEach((plan) => {
    if (plan.rules && Array.isArray(plan.rules)) {
      plan.rules.forEach((rule) => {
        if (rule.name === ruleName) {
          results.push(rule.value);
        }
      });
    }
  });

  return results;
}

const EffectiveDate = ({
  project,
  onNext,
  onBack,
  currentStatus,
  tracer,
  updateUrl,
}) => {
  const subscriptionId =
    localStorage.getItem(KEYS.SUBSCRIPTION_ID) ||
    get(project, "fields.currentSubscription");
  const insurancePlanId = useMemo(() => {
    return (
      get(
        get(project, "subscriptions", []).find(
          ({ id }) => id === subscriptionId,
        ),
        "insurancePlan.id",
      ) || get(project, "fields.lastSelected.0")
    );
  }, [project, subscriptionId]);
  const processId = useMemo(() => {
    const statuses = get(project, "statuses", []);
    if (statuses.length > 0) {
      return get(statuses[0], "status.process.id");
    }
    return null;
  }, [project]);

  const DEFAULT_DELTA_START = 1;
  const DEFAULT_DELTA_START_RIA = 40;
  const DEFAULT_DELTA_END_RIA = 0;
  const DEFAULT_DELTA_END = 0;
  const [loading, setLoading] = useState(false);
  const [deltas, setDeltas] = useState({
    start: { normal: DEFAULT_DELTA_START, ria: DEFAULT_DELTA_START_RIA },
    end: { normal: DEFAULT_DELTA_END, ria: DEFAULT_DELTA_END_RIA },
  });
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [buildGeneralCost] = useMutation(BUILD_GENERAL_COST);
  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS);
  const [generalCosts] = useLazyQuery(GENERAL_COSTS);
  const { data } = useQuery(SUBSCRIPTION, {
    variables: { where: { id: subscriptionId } },
  });

  const buildInsurancePlanFilter = useCallback(
    (issuanceDate) => {
      const issuanceDateMoment = moment(issuanceDate).startOf("day");
      const issuanceDateDifference = issuanceDateMoment.diff(
        moment().startOf("day"),
        "days",
      );

      const adherentAge = get(project, "contact.user.birthDate")
        ? moment(get(project, "contact.user.birthDate")).startOf("year")
        : null;
      const age = adherentAge ? moment().diff(adherentAge, "years") : null;
      const profession = get(project, "contact.profession");

      const [startKey, endKey] = project.ria
        ? [
            INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
            INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
          ]
        : [
            INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
            INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
          ];

      const sqlConditions = [
        generateSQLCondition(
          startKey,
          "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
          issuanceDateDifference,
        ),
        generateSQLCondition(
          endKey,
          `r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value ${
            issuanceDateMoment.year() === moment().year()
              ? "OR CAST(r.value AS NUMERIC) = 0"
              : ""
          }`,
          issuanceDateDifference,
        ),
        generateSQLCondition(
          INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
          "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
          age,
        ),
        generateSQLCondition(
          INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
          "r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value",
          age,
        ),
        generateSQLCondition(
          INSURANCE_PLAN_PARAMS_RULES.PROFESSION,
          `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
          `'${profession}'`,
        ),
        generateSQLCondition(
          INSURANCE_PLAN_PARAMS_RULES.PROCESSES,
          `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
          `'${processId}'`,
        ),
      ];

      return {
        raw: sqlConditions,
        custom: {
          AND: [
            {
              OR: [
                { startDate: null },
                {
                  startDate: issuanceDateMoment,
                  operation: "<=",
                },
              ],
            },
            {
              OR: [
                { endDate: null },
                {
                  endDate: issuanceDateMoment,
                  operation: ">=",
                },
              ],
            },
          ],
        },
      };
    },
    [project, processId],
  );

  const handlePriceChangeCancel = useMemo(() => {
    return async ({ previousDate }) => {
      try {
        // Ensure previousDate is in the correct format (ISO string)
        const issuanceDateISO =
          typeof previousDate === "string"
            ? previousDate
            : moment(previousDate).isValid()
              ? moment(previousDate).toISOString()
              : moment(previousDate).toISOString();

        // Step 1: Restore the project date in the database
        await updateProject({
          variables: {
            where: { id: project.id },
            data: {
              contract: {
                ...get(project, "contract", {}),
                issuanceDate: issuanceDateISO,
              },
            },
          },
        });

        // Step 2: Build the main product cost FIRST for the navbar
        // This ensures the navbar gets the correct cost immediately
        await buildGeneralCost({
          variables: {
            data: {
              project: { id: project.id },
              insurancePlan: { id: insurancePlanId },
            },
          },
        });

        // IMPORTANT: Update project object in memory AFTER building the cost
        // This ensures the navbar's useEffect triggers and refetches the generalCost with the restored cost
        if (project.contract) {
          project.contract.issuanceDate = issuanceDateISO;
        } else {
          project.contract = { issuanceDate: issuanceDateISO };
        }

        // Step 3: Build generalCosts for all products and upsells with the restored date
        const insurancePlanFilter = buildInsurancePlanFilter(issuanceDateISO);

        // Get all upsells from project
        const currentUpsells = get(project, "fields.upsells", {});
        const selectedPlanUpsells = get(currentUpsells, insurancePlanId, []);
        const selectedOffersIds = (selectedPlanUpsells || [])
          .map((upsell) => get(upsell, "insurancePlan.id"))
          .filter(Boolean);
        const allInsurancePlanIds = [
          insurancePlanId,
          ...selectedOffersIds,
        ].filter(Boolean);

        // Build generalCosts for main products
        await buildGeneralCosts({
          variables: {
            data: {
              project: { id: project.id },
              insurancePlanFilter,
            },
          },
        });

        // Build generalCosts for all upsells
        const allUpsellPlanIds = new Set();
        Object.values(currentUpsells).forEach((upsellArray) => {
          if (Array.isArray(upsellArray)) {
            upsellArray.forEach((upsell) => {
              const planId = get(upsell, "insurancePlan.id");
              if (planId) {
                allUpsellPlanIds.add(planId);
              }
            });
          }
        });

        if (allUpsellPlanIds.size > 0) {
          await buildGeneralCosts({
            variables: {
              data: {
                project: { id: project.id },
                insurancePlanFilter: {
                  ...insurancePlanFilter,
                  isIn: {
                    id: Array.from(allUpsellPlanIds),
                  },
                },
              },
            },
          });
        }

        // Step 4: Get all generalCosts to update upsells automatically
        const { data: generalCostsData } = await generalCosts({
          fetchPolicy: "no-cache",
          variables: {
            where: {
              project: { id: project.id },
            },
            isIn: {
              insurancePlan: {
                id: allInsurancePlanIds,
              },
            },
          },
        });

        const generalCostsList = omitDeep(
          get(generalCostsData, "generalCosts.data", []),
          "__typename",
        );

        // Step 5: Map existing upsells with rebuilt costs to ensure alignment
        const updatedUpsells = { ...currentUpsells };
        const upsellsCosts = generalCostsList.filter(
          (cost) => get(cost, "insurancePlan.id") !== insurancePlanId,
        );

        // Map existing upsells to rebuilt costs by insurancePlan.id
        const alignedUpsells = selectedPlanUpsells.map((existingUpsell) => {
          const existingUpsellPlanId = get(existingUpsell, "insurancePlan.id");
          const rebuiltCost = upsellsCosts.find(
            (cost) => get(cost, "insurancePlan.id") === existingUpsellPlanId,
          );
          return rebuiltCost || existingUpsell;
        });

        updatedUpsells[insurancePlanId] = alignedUpsells;

        // Step 6: Update project with aligned upsells
        await updateProject({
          variables: {
            where: { id: project.id },
            data: {
              fields: {
                ...get(project, "fields", {}),
                upsells: updatedUpsells,
              },
            },
          },
        });

        // Update the project object in memory
        if (project.fields) {
          project.fields.upsells = updatedUpsells;
        }
      } catch (error) {
        console.error(
          "Error restoring project date and rebuilding costs:",
          error,
        );
        throw error;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    project,
    updateProject,
    buildGeneralCost,
    buildGeneralCosts,
    generalCosts,
    buildInsurancePlanFilter,
    processId,
    insurancePlanId,
  ]);

  const handlePriceChangeConfirm = useMemo(() => {
    return async ({ newDate, newProducts, newUpsells, insurancePlanId }) => {
      try {
        // Ensure newDate is in the correct format (ISO string)
        const issuanceDateISO =
          typeof newDate === "string"
            ? newDate
            : moment(newDate).isValid()
              ? moment(newDate).toISOString()
              : moment(newDate).toISOString();

        // Step 1: Update the project with the new date
        await updateProject({
          variables: {
            where: { id: project.id },
            data: {
              contract: {
                ...get(project, "contract", {}),
                issuanceDate: issuanceDateISO,
              },
            },
          },
        });

        // Step 2: Build generalCosts for all products and upsells with the new date
        const insurancePlanFilter = buildInsurancePlanFilter(issuanceDateISO);

        // Get all upsells from project
        const currentUpsells = get(project, "fields.upsells", {});
        const selectedPlanUpsells = get(currentUpsells, insurancePlanId, []);
        const selectedOffersIds = (selectedPlanUpsells || [])
          .map((upsell) => get(upsell, "insurancePlan.id"))
          .filter(Boolean);
        const allInsurancePlanIds = [
          insurancePlanId,
          ...selectedOffersIds,
        ].filter(Boolean);

        // Build generalCosts for main products
        // Build generalCosts for main products
        await buildGeneralCosts({
          variables: {
            data: {
              project: { id: project.id },
              insurancePlanFilter,
            },
          },
        });

        // Build generalCosts for all upsells
        const allUpsellPlanIds = new Set();
        Object.values(currentUpsells).forEach((upsellArray) => {
          if (Array.isArray(upsellArray)) {
            upsellArray.forEach((upsell) => {
              const planId = get(upsell, "insurancePlan.id");
              if (planId) {
                allUpsellPlanIds.add(planId);
              }
            });
          }
        });

        if (allUpsellPlanIds.size > 0) {
          await buildGeneralCosts({
            variables: {
              data: {
                project: { id: project.id },
                insurancePlanFilter: {
                  ...insurancePlanFilter,
                  isIn: {
                    id: Array.from(allUpsellPlanIds),
                  },
                },
              },
            },
          });
        }

        // Step 3: Get all generalCosts to update upsells automatically
        const { data: generalCostsData } = await generalCosts({
          fetchPolicy: "no-cache",
          variables: {
            where: {
              project: { id: project.id },
            },
            isIn: {
              insurancePlan: {
                id: allInsurancePlanIds,
              },
            },
          },
        });

        const generalCostsList = omitDeep(
          get(generalCostsData, "generalCosts.data", []),
          "__typename",
        );

        // Step 4: Map existing upsells with rebuilt costs to ensure alignment
        const updatedUpsells = { ...currentUpsells };
        const upsellsCosts = generalCostsList.filter(
          (cost) => get(cost, "insurancePlan.id") !== insurancePlanId,
        );

        // Map existing upsells to rebuilt costs by insurancePlan.id
        const alignedUpsells = selectedPlanUpsells.map((existingUpsell) => {
          const existingUpsellPlanId = get(existingUpsell, "insurancePlan.id");
          const rebuiltCost = upsellsCosts.find(
            (cost) => get(cost, "insurancePlan.id") === existingUpsellPlanId,
          );
          return rebuiltCost || existingUpsell;
        });

        updatedUpsells[insurancePlanId] = alignedUpsells;

        // Step 5: Update project with aligned upsells
        await updateProject({
          variables: {
            where: { id: project.id },
            data: {
              fields: {
                ...get(project, "fields", {}),
                upsells: updatedUpsells,
              },
            },
          },
        });

        // Update the project object in memory
        project.contract.issuanceDate = issuanceDateISO;
        if (project.fields) {
          project.fields.upsells = updatedUpsells;
        }
      } catch (error) {
        console.error(
          "Error updating project with new date and upsells:",
          error,
        );
        throw error;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    project,
    updateProject,
    buildGeneralCosts,
    generalCosts,
    buildInsurancePlanFilter,
    processId,
    insurancePlanId,
  ]);

  const previousDateRef = useRef(get(project, "contract.issuanceDate"));
  const setFieldValueRef = useRef(null);

  const {
    handleEffectiveDateChange,
    loading: priceLoading,
    cancelling,
    modalVisible,
    priceComparison,
    handleConfirm: handlePriceConfirm,
    handleCancel: handlePriceCancel,
  } = useEffectiveDatePriceChange({
    project,
    processId,
    onConfirm: handlePriceChangeConfirm,
    onCancel: handlePriceChangeCancel,
    insurancePlanId,
  });

  const handleConfirm = useCallback(async () => {
    await handlePriceConfirm();
    // Update previous date ref after confirmation
    if (setFieldValueRef.current) {
      const currentDate = get(project, "contract.issuanceDate");
      previousDateRef.current = currentDate;
    }
  }, [handlePriceConfirm, project]);

  const handleCancel = useCallback(async () => {
    const setFieldValue = setFieldValueRef.current;
    const previousDate =
      previousDateRef.current || get(project, "contract.issuanceDate");
    await handlePriceCancel(setFieldValue, previousDate);
  }, [handlePriceCancel, project]);
  useQuery(INSURANCE_PLAN, {
    variables: {
      where: {
        id: insurancePlanId,
      },
    },
    onCompleted: ({ insurancePlan }) => {
      const deltaStartRia =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
          }),
        ) || DEFAULT_DELTA_START_RIA;
      const deltaStart =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
          }),
        ) || DEFAULT_DELTA_START;
      const deltaEnd =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
          }),
        ) || DEFAULT_DELTA_END;
      const deltaEndRia =
        max(
          findRules({
            params: get(insurancePlan, "params"),
            ruleName: INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
          }),
        ) || DEFAULT_DELTA_END_RIA;
      setDeltas({
        start: { normal: deltaStart, ria: deltaStartRia },
        end: { normal: deltaEnd, ria: deltaEndRia },
      });
    },
  });

  const onSubmit = ({ contract, ria, fields, terminationRequest }) => {
    setLoading(true);
    const source = localStorage.getItem(KEYS.SOURCE);
    updateUrl({
      subscriptionId,
      url: get(
        data,
        "subscription.insurancePlan.config.generatedUrl",
        `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
      ),
    });
    onNext({
      payload: {
        contract: {
          ...contract,
          caseType: ria
            ? CASE_TYPES.RETURN_TO_COMPETITION
            : CASE_TYPES.NEW_CONTRACT,
        },
        ria,
        fields,
        terminationRequest,
      },
      onCompleted: () => setLoading(false),
    });
  };

  if (loading) return <Loader />;

  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
        showCart
        priceLoading={priceLoading || cancelling}
      />
      <View
        contract={get(project, "contract", {})}
        fields={get(project, "fields", {})}
        terminationRequest={get(project, "terminationRequest", {})}
        ria={project?.ria}
        locked={project?.locked}
        deltas={deltas}
        onSubmit={onSubmit}
        onBack={onBack}
        onEffectiveDateChange={(newDate, setFieldValue, currentDate) => {
          setFieldValueRef.current = setFieldValue;
          previousDateRef.current =
            currentDate || get(project, "contract.issuanceDate");
          handleEffectiveDateChange(newDate, setFieldValue, currentDate);
        }}
        priceLoading={priceLoading}
        modalVisible={modalVisible}
        setFieldValueRef={setFieldValueRef}
        initialContract={get(project, "contract", {})}
      />
      <PriceChangeModal
        visible={modalVisible}
        priceComparison={priceComparison}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        loading={priceLoading}
        cancelling={cancelling}
        ButtonComponent={Button}
      />
    </>
  );
};

export default EffectiveDate;
